/**
 * YieldChart — minimalist SVG line chart of the projected monthly capital
 * curve from t=0 (principal) to t=duration (maturity value). Pure SVG,
 * zero deps. Uses the brand bronze palette and a subtle gradient fill
 * under the curve so the dashboard widget stays editorial-feeling.
 */
import { useMemo } from "react";

const W = 600;
const H = 180;
const PAD_X = 40;
const PAD_TOP = 20;
const PAD_BOTTOM = 24;

export default function YieldChart({
  principal,
  netProfit,
  months,
  // For "open-ended", we still chart 12 indicative months.
  chartMonths,
  locale = "it",
}) {
  const m = Math.max(2, chartMonths || months || 12);
  const finalValue = principal + netProfit;
  const step = netProfit / m;

  const data = useMemo(() => {
    const points = [];
    for (let i = 0; i <= m; i++) {
      points.push({ x: i, y: principal + step * i });
    }
    return points;
  }, [principal, step, m]);

  const yMin = principal;
  const yMax = finalValue;
  const ySpan = Math.max(1, yMax - yMin);

  const sx = (i) => PAD_X + (i / m) * (W - PAD_X * 2);
  const sy = (v) =>
    H - PAD_BOTTOM - ((v - yMin) / ySpan) * (H - PAD_TOP - PAD_BOTTOM);

  // Smooth path using cubic mid-point control points
  const pathD = useMemo(() => {
    if (data.length < 2) return "";
    const _sx = (i) => PAD_X + (i / m) * (W - PAD_X * 2);
    const _sy = (v) =>
      H - PAD_BOTTOM - ((v - yMin) / ySpan) * (H - PAD_TOP - PAD_BOTTOM);
    let d = `M ${_sx(data[0].x).toFixed(2)} ${_sy(data[0].y).toFixed(2)}`;
    for (let i = 1; i < data.length; i++) {
      const x0 = _sx(data[i - 1].x);
      const y0 = _sy(data[i - 1].y);
      const x1 = _sx(data[i].x);
      const y1 = _sy(data[i].y);
      const cx = (x0 + x1) / 2;
      d += ` C ${cx.toFixed(2)} ${y0.toFixed(2)}, ${cx.toFixed(2)} ${y1.toFixed(2)}, ${x1.toFixed(2)} ${y1.toFixed(2)}`;
    }
    return d;
  }, [data, m, yMin, ySpan]);

  const fillD = pathD
    ? `${pathD} L ${sx(m)} ${H - PAD_BOTTOM} L ${sx(0)} ${H - PAD_BOTTOM} Z`
    : "";

  // Y-axis ticks (3): start, mid, end
  const ticks = [yMin, (yMin + yMax) / 2, yMax];

  // X-axis labels (start / mid / end, in months from now)
  const xLabels = [0, Math.floor(m / 2), m];

  return (
    <div data-testid="yield-chart" className="w-full" style={{ overflowX: "auto" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Projected monthly capital growth"
      >
        <defs>
          <linearGradient id="yc-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--br-bronze)" stopOpacity="0.32" />
            <stop offset="100%" stopColor="var(--br-bronze)" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="yc-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--br-bronze-soft)" />
            <stop offset="100%" stopColor="var(--br-bronze)" />
          </linearGradient>
        </defs>

        {/* Horizontal grid */}
        {ticks.map((tick, i) => (
          <line
            key={i}
            x1={PAD_X}
            x2={W - PAD_X}
            y1={sy(tick)}
            y2={sy(tick)}
            stroke="var(--br-line)"
            strokeWidth={i === 0 ? 1 : 0.5}
            strokeDasharray={i === 0 ? undefined : "2 3"}
          />
        ))}

        {/* Area fill */}
        {fillD && <path d={fillD} fill="url(#yc-fill)" />}

        {/* Curve */}
        {pathD && (
          <path
            d={pathD}
            fill="none"
            stroke="url(#yc-line)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        )}

        {/* Endpoint dot */}
        <circle
          cx={sx(m)}
          cy={sy(finalValue)}
          r="4.5"
          fill="var(--br-bronze)"
          stroke="var(--br-midnight)"
          strokeWidth="1.5"
        />

        {/* Y-axis labels (currency, abbreviated) */}
        {ticks.map((tick, i) => (
          <text
            key={i}
            x={PAD_X - 6}
            y={sy(tick) + 3}
            textAnchor="end"
            fontFamily="ui-monospace, monospace"
            fontSize="9"
            fill="var(--br-text-dim)"
          >
            {abbrevEUR(tick, locale)}
          </text>
        ))}

        {/* X-axis labels (months) */}
        {xLabels.map((mi) => (
          <text
            key={mi}
            x={sx(mi)}
            y={H - 6}
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
            fontSize="9"
            fill="var(--br-text-dim)"
          >
            {mi === 0 ? "t₀" : `+${mi}m`}
          </text>
        ))}
      </svg>
    </div>
  );
}

function abbrevEUR(value) {
  if (value >= 1000) {
    const k = value / 1000;
    return `€${k.toFixed(k < 10 ? 1 : 0)}K`;
  }
  return `€${Math.round(value)}`;
}
